/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import React, {
	FunctionComponent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from "react";
import { IViewInstance, ViewService } from "../../view/ViewService";
import { DROPDOWN_STYLE, DropdownMenu, IDropdownMenuItem } from "./DropdownMenu";
import { FC_ANCHOR } from "./FloatingContainer";
import { Icon, ICON_SIZE } from "./Icon";
import { useBodyClick, useStateWithEvent } from "../hooks";

const SANITIZE_REGEX = /[.:]/g;

interface ITabItemProps {
	viewInstance: IViewInstance;
	isActive: boolean;
	isInitial: boolean;
	shouldRemove: boolean;
	onSelect: () => void;
	onClose: () => void;
	onSortStart?: (viewInstance: IViewInstance) => void;
	onSortEnd?: () => void;
	onSortOver?: (viewInstance: IViewInstance, offset: number) => void;
	onSortOut?: (viewInstance: IViewInstance) => void;
	isSorting?: boolean;
	forwardRef: React.MutableRefObject<HTMLLIElement>;
}

export const TabItem: FunctionComponent<ITabItemProps> = ({
	viewInstance,
	isActive,
	isInitial,
	shouldRemove,
	onSelect,
	onClose,
	onSortStart,
	onSortEnd,
	onSortOver,
	onSortOut,
	isSorting,
	forwardRef
}) => {
	const [ show, setShow ] = useState(() => (isInitial ? true : false));
	const viewInfo = useStateWithEvent(viewInstance?.view?.onViewInfoChanged, () =>
		viewInstance?.view?.getViewInfo()
	);

	const doClose = useCallback(
		(ev: React.MouseEvent) => {
			if (viewInfo.canClose) {
				ev.stopPropagation();
				onClose();
			}
		},
		[ viewInfo.canClose, onClose ]
	);

	const onDragStart = useCallback(() => {
		forwardRef.current?.classList.add("edt-main-toolbar__tab-list-item--sorting");
		onSortStart?.(viewInstance);
	}, [ forwardRef, viewInstance, onSortStart ]);

	const onDragEnd = useCallback(() => {
		forwardRef.current?.classList.remove("edt-main-toolbar__tab-list-item--sorting");
		onSortEnd?.();
	}, [ forwardRef, onSortEnd ]);

	const onDragOver = useCallback((ev: React.DragEvent) => {
		if (!forwardRef.current) {
			return;
		}

		if (!isSorting) {
			onSelect();
			return;
		}

		ev.preventDefault();
		ev.dataTransfer.dropEffect = "move";

		// Calculate offset to the element center and resolve offset -1 or 1
		const rect = forwardRef.current.getBoundingClientRect();
		const offset = ev.clientX - rect.left > rect.width / 2 ? 1 : -1;

		onSortOver?.(viewInstance, offset);
	}, [ forwardRef, onSortOver, isSorting ]);

	const onDrop = useCallback((ev: React.DragEvent) => {
		if (isSorting) {
			ev.preventDefault();
			onSortEnd?.();
		}
	}, [ isSorting ]);

	const onDragEnter = useCallback((ev: React.DragEvent) => {
		if (ev.target === forwardRef.current) {
			forwardRef.current.classList.add("edt-main-toolbar__tab-list-item--sorting-over");
		}
	}, [ forwardRef ]);

	const onDragLeave = useCallback((ev: React.DragEvent) => {
		if (ev.target === forwardRef.current) {
			forwardRef.current.classList.remove("edt-main-toolbar__tab-list-item--sorting-over");
			onSortOut?.(viewInstance);
		}
	}, [ forwardRef, onSortOut, viewInstance ]);

	const itemClass = BEM("edt-main-toolbar", "tab-list-item", {
		active: isActive,
		"can-close": viewInfo.canClose,
		"is-modified": viewInfo.isModified,
		show: show && !shouldRemove
	});

	// Change show state after a while to trigger animation
	useEffect(() => {
		if (isInitial) {
			return;
		}

		const t = setTimeout(() => {
			setShow(true);
		}, 10);

		return () => {
			clearTimeout(t);
		};
	}, [ isInitial ]);

	itemClass.add("m-view-tab-id-" + viewInstance.id.replace(SANITIZE_REGEX, "_"));
	itemClass.add("m-view-tab-name-" + viewInstance.name.replace(SANITIZE_REGEX, "_"));

	if (viewInfo.tabStyleModifier) {
		itemClass.mod(viewInfo.tabStyleModifier);
	}

	return (
		<li
			key={viewInstance.id}
			className={itemClass.toClassName()}
			onClick={onSelect}
			onDragStart={onDragStart}
			onDragEnd={onDragEnd}
			onDragOver={onDragOver}
			onDrop={onDrop}
			onDragEnter={onDragEnter}
			onDragLeave={onDragLeave}
			draggable={true}
			title={viewInfo.label}
			ref={forwardRef}
		>
			<span className={itemClass.element("tab-list-item-corner", "left").toClassName()} />
			<span className={itemClass.element("tab-list-item-corner", "right").toClassName()} />
			<span className={itemClass.element("tab-list-item-inner").toClassName()}>
				<button
					className={itemClass.element("tab-list-item-button").toClassName()}
					onClick={onSelect}
				>
					{viewInfo.icon ? (
						<span className={itemClass.element("tab-list-item-icon").toClassName()}>
							<Icon size={ICON_SIZE.CUSTOM} name={viewInfo.icon} />
						</span>
					) : null}
					{viewInfo.label ? (
						<span className={itemClass.element("tab-list-item-label").toClassName()}>
							{viewInfo.label}
						</span>
					) : null}
				</button>
				{viewInfo.canClose || viewInfo.isModified ? (
					<button
						className={itemClass.element("tab-list-item-close").toClassName()}
						onClick={doClose}
					>
						<span className={itemClass.element("tab-list-item-close-dot").toClassName()} />
						<Icon size={ICON_SIZE.CUSTOM} name="mdi/close" />
					</button>
				) : null}
			</span>
		</li>
	);
};

interface IMainToolbarTabListProps {
	viewService: ViewService;
}

export const MainToolbarTabList: FunctionComponent<IMainToolbarTabListProps> = ({ viewService }) => {
	const [ items, setItems ] = useState(() => {
		const viewList = viewService.getViewInstanceList();

		return viewList.map((viewInstance) => ({
			viewInstance: viewInstance,
			isInital: true,
			shouldRemove: false,
			elRef: React.createRef<HTMLLIElement>()
		}));
	});

	const [ currentId, setCurrentId ] = useState(() => viewService.getCurrentViewId());

	const [ showMoreMenu, setShowMoreMenu ] = useState(false);
	const [ isMoreMenuOpened, setIsMoreMenuOpened ] = useState(false);

	const [ isSorting, setIsSorting ] = useState(false);
	const sortInstance = useRef<IViewInstance|null>(null);
	const sortTarget = useRef<{ instance: IViewInstance, offset: number }|null>(null);
	const sortCursorElRef = useRef<HTMLDivElement>();

	const scrollContainerRef = useRef<HTMLDivElement>();
	const moreBtnRef = useRef();
	const preventScroll = useRef(false);

	const scrollToItem = useCallback(
		(id: string) => {
			const localItemElement = items.filter((x) => x.viewInstance.id === id)[0];

			if (localItemElement && localItemElement.elRef.current) {
				scrollContainerRef.current?.scrollTo({
					behavior: "smooth",
					left: localItemElement.elRef.current.offsetLeft - 10
				});

				// Call again after timeout because now we have final width
				setTimeout(() => {
					scrollContainerRef.current?.scrollTo({
						behavior: "smooth",
						left: localItemElement.elRef.current.offsetLeft - 10
					});
				}, 350);
			}
		},
		[ items ]
	);

	const handleScroll = useCallback<React.WheelEventHandler<HTMLElement>>((event) => {
		scrollContainerRef.current.scrollLeft += event.deltaY + event.deltaX;
	}, []);

	useEffect(() => {
		const removeTimeouts = [];

		const updateItems = () =>
			setItems((prevItems) => {
				const viewList = viewService.getViewInstanceList();
				const newItems = prevItems.slice();

				const prevItemIndex = new Set<string>();
				const listItemIndex = new Set<string>();

				const removeList = [];

				// Create indexes for simpler comparision
				for (let i = 0; i < prevItems.length; i++) {
					prevItemIndex.add(prevItems[i].viewInstance.id);
				}

				for (let i = 0; i < viewList.length; i++) {
					listItemIndex.add(viewList[i].id);
				}

				// Add missing items
				for (let i = 0; i < viewList.length; i++) {
					if (!prevItemIndex.has(viewList[i].id)) {
						newItems.push({
							viewInstance: viewList[i],
							isInital: false,
							shouldRemove: false,
							elRef: React.createRef<HTMLLIElement>()
						});
					}
				}

				// Remove redundant items
				for (let i = 0; i < newItems.length; i++) {
					if (!listItemIndex.has(newItems[i].viewInstance.id)) {
						newItems[i].shouldRemove = true;
						removeList.push(newItems[i]);
					}
				}

				if (removeList.length > 0) {
					const rmvList = [ ...removeList ];
					const t = setTimeout(() => {
						setItems((_prevItems) => {
							const _newItems = _prevItems.slice();

							rmvList.forEach((_item) => {
								const _i = _newItems.indexOf(_item);

								if (_i >= 0) {
									_newItems.splice(_i, 1);
								}
							});

							return _newItems;
						});

						const ti = removeTimeouts.indexOf(t);

						if (ti >= 0) {
							removeList.splice(ti, 1);
						}
					}, 500);

					removeTimeouts.push(t);
				}

				// Sort new items
				// Temporal solution because of deleted items that can't be sorted because they are not in the new list
				if (newItems.length === viewList.length) {
					newItems.sort((a, b) => {
						return viewList.indexOf(a.viewInstance) - viewList.indexOf(b.viewInstance);
					});
				}

				return newItems;
			});

		const updateCurrentId = () => setCurrentId(viewService.getCurrentViewId());

		onEvent(viewService.onViewListChanged, updateItems);
		onEvent(viewService.onCurrentViewChanged, updateCurrentId);

		return () => {
			offEvent(viewService.onViewListChanged, updateItems);
			offEvent(viewService.onCurrentViewChanged, updateCurrentId);

			removeTimeouts.forEach((t) => clearTimeout(t));
		};
	}, [ viewService ]);

	useEffect(() => {
		const handleEvent = () => {
			const shouldShow =
				scrollContainerRef.current.scrollWidth > scrollContainerRef.current.clientWidth;

			if (shouldShow && !showMoreMenu) {
				setShowMoreMenu(true);
			} else if (!shouldShow && showMoreMenu) {
				setShowMoreMenu(false);
			}
		};

		handleEvent();
		window.addEventListener("resize", handleEvent);

		return () => {
			window.removeEventListener("resize", handleEvent);
		};
	}, [ items, showMoreMenu ]);

	useLayoutEffect(() => {
		if (preventScroll.current === true) {
			preventScroll.current = false;
			return;
		}

		scrollToItem(currentId);
	}, [ currentId, preventScroll ]);

	const onMoreButtonClick = useCallback((ev: React.MouseEvent<HTMLElement>) => {
		setIsMoreMenuOpened((x) => !x);
		ev.stopPropagation();
	}, []);

	const onMoreMenuItemClick = useCallback((item: IDropdownMenuItem) => {
		viewService.select(item.id);
		setIsMoreMenuOpened(false);
	}, []);

	const onMoreMenuClose = useCallback(() => {
		setIsMoreMenuOpened(false);
	}, []);

	const onItemSortStart = useCallback((viewInstance: IViewInstance) => {
		if (sortInstance.current) {
			return;
		}

		sortInstance.current = viewInstance;
		setIsSorting(true);
	}, [ sortInstance ]);

	const onItemSortEnd = useCallback(() => {
		if (sortInstance.current && sortTarget.current.instance) {
			viewService.sort(sortInstance.current.id, sortTarget.current.instance.id, sortTarget.current.offset);
		}

		sortInstance.current = null;
		sortTarget.current = null;
		sortCursorElRef.current.style.display = "";
		setIsSorting(false);
	}, []);

	const onItemSortOver = useCallback((viewInstance: IViewInstance, offset: number) => {
		sortTarget.current = { instance: viewInstance, offset };
		
		const tabItem = items.filter((x) => x.viewInstance.id === viewInstance.id)[0];
		const tabEl = tabItem.elRef.current;
		
		if (tabEl && sortCursorElRef.current) {
			sortCursorElRef.current.style.display = "block";
			sortCursorElRef.current.style.left = (tabEl.offsetLeft + (offset <= 0 ? 0 : tabEl.offsetWidth) - 1) + "px";
		}
	}, []);

	const onItemSortOut = useCallback((viewInstance: IViewInstance) => {
		setTimeout(() => {
			if (sortTarget && sortTarget.current.instance === viewInstance) {
				sortTarget.current = null;
				sortCursorElRef.current.style.display = "";
			}
		}, 10);
	}, [ sortTarget ]);

	useBodyClick(() => setIsMoreMenuOpened(false), isMoreMenuOpened);

	const baseClass = BEM("edt-main-toolbar", "tab-list");

	return (
		<nav className={baseClass.toClassName()}>
			<div
				className={baseClass.element("tab-list-scroll").toClassName()}
				ref={scrollContainerRef}
				onWheel={handleScroll}
			>
				<ul className={baseClass.element("tab-list-container").toClassName()}>
					{items.map((item) => {
						return (
							<TabItem
								key={item.viewInstance.id}
								viewInstance={item.viewInstance}
								isActive={item.viewInstance.id === currentId}
								isInitial={item.isInital}
								shouldRemove={item.shouldRemove}
								isSorting={isSorting}
								onSortStart={onItemSortStart}
								onSortEnd={onItemSortEnd}
								onSortOver={onItemSortOver}
								onSortOut={onItemSortOut}
								onSelect={() => {
									preventScroll.current = true;
									viewService.select(item.viewInstance.id);
									item.elRef.current?.scrollIntoView({ behavior: "smooth" });
								}}
								onClose={() => {
									preventScroll.current = true;
									viewService.close(item.viewInstance.id);
								}}
								forwardRef={item.elRef}
							/>
						);
					})}
					<div
						className={baseClass.element("tab-list-sort-cursor").toClassName()}
						ref={sortCursorElRef}
					/>
				</ul>
			</div>
			<button
				className={baseClass.element("tab-list-more", { show: showMoreMenu }).toClassName()}
				onClick={onMoreButtonClick}
				ref={moreBtnRef}
			>
				<Icon name="mdi/dots-horizontal" size={ICON_SIZE.SMALL} />
				<DropdownMenu
					parent={moreBtnRef}
					isOpened={isMoreMenuOpened}
					anchor={FC_ANCHOR.RIGHT_BOTTOM}
					offset={5}
					style={DROPDOWN_STYLE.COMPACT}
					items={items.map((item) => {
						const viewInfo = item.viewInstance.view.getViewInfo();

						return {
							id: item.viewInstance.id,
							label: viewInfo.label,
							icon: viewInfo.icon
						};
					})}
					onItemClick={onMoreMenuItemClick}
					onClose={onMoreMenuClose}
				/>
			</button>
		</nav>
	);
};
